import { Link } from 'react-router-dom';
import { Ruler, AlertCircle } from 'lucide-react';

export default function MeasuringGuide() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-16 lg:pt-20">
      <div className="bg-[#111111] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="flex items-center gap-3 mb-4">
            <Ruler size={18} className="text-orange-500" />
            <span className="font-inter text-xs text-orange-500 uppercase tracking-widest">Resources</span>
          </div>
          <h1 data-testid="measuring-heading" className="font-bebas text-5xl lg:text-6xl text-white tracking-wider mb-2">
            MEASURING INFORMATION
          </h1>
          <p className="font-inter text-sm text-zinc-500">How to correctly measure your barrel for a custom clamp-on muzzle brake</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12 prose-custom">
        <div className="bg-orange-500/10 border border-orange-500/30 p-5 mb-8 flex items-start gap-3">
          <AlertCircle size={18} className="text-orange-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-inter text-sm font-semibold text-orange-400 mb-1">Accuracy Matters</p>
            <p className="font-inter text-sm text-zinc-400">
              Inaccurate measurements are the number one cause of fitment issues. Take your time and measure twice. If in doubt, use calipers rather than a standard ruler.
            </p>
          </div>
        </div>

        {[
          {
            step: '01',
            title: 'Barrel Outside Diameter',
            content: 'Measure the outside diameter of the barrel at the point where you want the muzzle brake to sit — typically at or near the muzzle end. Measure in inches with a caliper for best accuracy. We need this measurement to ensure the brake clamps tightly without damaging the barrel.',
          },
          {
            step: '02',
            title: 'Location of Measurement',
            content: 'For tapered barrels, measure at the location where the front of the brake will rest. The brake spans approximately 3.5 inches back from the muzzle. If there is a sight, taper change, or other obstruction within that span, note it in your order.',
          },
          {
            step: '03',
            title: 'Confirm Your Caliber',
            content: 'Always confirm your caliber before ordering. The bore diameter of the muzzle brake must be correct for your caliber. When in doubt, over-specify — for example, note ".308 Win / 7.62x51" rather than just ".30 caliber."',
          },
          {
            step: '04',
            title: 'Smooth Barrel Required',
            content: 'Our clamp-on brakes require a smooth barrel (no sights, no interrupted surfaces) in the mounting area. If your barrel has front sights, a gas block, or other protrusions in the mounting zone, you may need a threaded brake instead.',
          },
          {
            step: '05',
            title: 'Submit Your Measurements',
            content: 'When placing your order, enter your caliber and barrel diameter in the order notes. You\'ll receive a confirmation email once your custom brake ships.',
          },
        ].map((s, i) => (
          <div key={i} className="flex gap-6 mb-8 bg-[#111111] border border-white/10 p-6">
            <div className="font-bebas text-4xl text-orange-500/30 tracking-widest leading-none flex-shrink-0 w-14 text-center">
              {s.step}
            </div>
            <div>
              <h3 className="font-bebas text-xl text-white tracking-wider mb-2">{s.title}</h3>
              <p className="font-inter text-sm text-zinc-400 leading-relaxed">{s.content}</p>
            </div>
          </div>
        ))}

        <div className="bg-[#111111] border border-white/10 p-6 text-center">
          <h2 className="font-bebas text-2xl text-white tracking-wider mb-3">READY TO ORDER?</h2>
          <p className="font-inter text-sm text-zinc-400 mb-5">Browse our custom clamp-on muzzle brakes, available for virtually any barrel diameter and caliber.</p>
          <Link to="/clamp-ons" data-testid="measuring-shop-link" className="inline-block font-bebas text-sm tracking-widest bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 transition-colors">
            SHOP CLAMP-ONS →
          </Link>
        </div>
      </div>
    </div>
  );
}
